import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'
import { viewEarningDetails } from '../../slice/earningSlice'
import { getCardName } from '../../common/utils'

const ViewEarningDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const earningId = location.state?.earningId
    const { earningDetail } = useSelector(state => state.earning)

    useEffect(() => {
        if (!earningId) { navigate('/earnings'); return }
        dispatch(viewEarningDetails({ id: earningId }))
    }, [earningId, dispatch, navigate])

    if (!earningDetail) {
        return (
            <div className="text-center py-5">
                <i className="fe-alert-circle" style={{ fontSize: 48, color: '#dc3545' }}></i>
                <h5 className="mt-3">No data found</h5>
                <button className="btn btn-primary mt-3" onClick={() => navigate('/earnings')}>
                    Go Back to Earnings
                </button>
            </div>
        )
    }

    const {
        id, tutor, parent, subscription_plan, parent_transaction,
        earning_type, amount, currency, payment_method,
        purchase_date, expiry_date, is_deleted, createdAt, updatedAt,
    } = earningDetail

    const isSession = earning_type === 'session'
    const person = tutor || parent
    const displayName = person
        ? `${person.first_name || ''} ${person.last_name || ''}`.trim()
        : 'Unknown User'
    const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

    // const currencySymbols = { INR: '₹', USD: '$', AED: 'د.إ', EUR: '€' }
    // const sym = currencySymbols[currency] || ''
    const sym = currency || ''
    const fmt = (val) => val ? `${sym} ${parseFloat(val).toLocaleString('en', { minimumFractionDigits: 2 })}` : 'N/A'
    const fmtMethod = (m) => m?.replace(/_/g, ' ').replace(/\bvc\b/gi, 'Visa card').replace(/\b\w/g, c => c.toUpperCase()) || 'N/A'
    const fmtDate = (d) => d ? moment(d).format('DD MMM YYYY, hh:mm A') : 'N/A'

    const sb = parent_transaction?.session_booking

    return (
        <div className="container-fluid px-4 py-4">

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Earning detail</h4>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/earnings')}>
                    ← Back to list
                </button>
            </div>

            {/* Profile */}
            <div className="card border shadow-sm mb-3" style={{ borderRadius: 12 }}>
                <div className="card-body d-flex align-items-center gap-3 p-4">
                    {person?.profile_picture ? (
                        <img src={person.profile_picture} alt="Profile"
                            style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', border: '1px solid #dee2e6' }} />
                    ) : (
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%', background: '#e8f0fe',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 16, fontWeight: 500, color: '#1a5ccf',
                        }}>{initials}</div>
                    )}
                    <div>
                        <h5 className="mb-1 fw-semibold">{displayName}</h5>
                        <p className="text-muted mb-2" style={{ fontSize: 13 }}>{person?.email || 'N/A'}</p>
                        <div className="d-flex gap-2 flex-wrap">
                            <span className="badge bg-success-subtle text-success">
                                Active {tutor ? 'tutor' : 'parent'}
                            </span>
                            <span className="badge bg-info-subtle text-info text-capitalize">
                                {earning_type}
                            </span>
                            {is_deleted === 1 && <span className="badge bg-danger">Deleted</span>}
                        </div>
                    </div>
                </div>
            </div>

            {/* Metric Cards */}
            <div className="row g-3 mb-3">
                <div className="col-4">
                    <div className="card border-0 h-100" style={{ background: '#f8f9fa', borderRadius: 10 }}>
                        <div className="card-body p-3">
                            <p className="text-muted mb-1" style={{ fontSize: 12 }}>
                                {isSession ? 'Admin earning' : 'Amount paid'}
                            </p>
                            <p className="mb-0 fw-semibold" style={{ fontSize: 19 }}>{fmt(amount)}</p>
                            <p className="text-muted mb-0" style={{ fontSize: 11 }}>{currency}</p>
                        </div>
                    </div>
                </div>
                <div className="col-4">
                    <div className="card border-0 h-100" style={{ background: '#f8f9fa', borderRadius: 10 }}>
                        <div className="card-body p-3">
                            {isSession ? (
                                <>
                                    <p className="text-muted mb-1" style={{ fontSize: 12 }}>Total paid</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: 19 }}>
                                        {fmt(parent_transaction?.amount)}
                                    </p>
                                    <p className="text-muted mb-0" style={{ fontSize: 11 }}>by parent</p>
                                </>
                            ) : (
                                <>
                                    <p className="text-muted mb-1" style={{ fontSize: 12 }}>Plan duration</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: 19, textTransform: 'capitalize' }}>
                                        {subscription_plan?.duration_type || 'N/A'}
                                    </p>
                                    <p className="text-muted mb-0" style={{ fontSize: 11 }}>{subscription_plan?.name}</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-4">
                    <div className="card border-0 h-100" style={{ background: '#f8f9fa', borderRadius: 10 }}>
                        <div className="card-body p-3">
                            <p className="text-muted mb-1" style={{ fontSize: 12 }}>Payment via</p>
                            <p className="mb-0 fw-semibold" style={{ fontSize: 14, paddingTop: 4 }}>
                                {getCardName(payment_method?.toUpperCase())}
                            </p>
                            <p className="text-muted mb-0" style={{ fontSize: 11 }}>
                                {isSession ? 'Card payment' : 'In-app purchase'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Session-specific: Booking + Fee breakdown */}
            {isSession && sb && (
                <div className="card border shadow-sm mb-3" style={{ borderRadius: 12 }}>
                    <div className="card-body p-4">
                        <p className="text-uppercase text-muted fw-semibold mb-3" style={{ fontSize: 11, letterSpacing: '0.05em' }}>
                            Session booking
                        </p>
                        {[
                            { label: 'Order ID', value: sb.order_id },
                            { label: 'Session type', value: sb.session_type },
                            { label: 'Total sessions', value: sb.total_sessions },
                            { label: 'Price per session', value: fmt(sb.price_per_session) },
                            { label: 'Payment status', value: sb.payment_status },
                            { label: 'Booking status', value: sb.status },
                        ].map(({ label, value }) => (
                            <div key={label} className="d-flex justify-content-between py-2 border-bottom">
                                <span className="text-muted" style={{ fontSize: 13 }}>{label}</span>
                                <span className="fw-medium" style={{ fontSize: 13, textTransform: 'capitalize' }}>{value ?? 'N/A'}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {isSession && parent_transaction && (
                <div className="card border shadow-sm mb-3" style={{ borderRadius: 12 }}>
                    <div className="card-body p-4">
                        <p className="text-uppercase text-muted fw-semibold mb-3" style={{ fontSize: 11, letterSpacing: '0.05em' }}>
                            Transaction fee breakdown
                        </p>
                        {[
                            { label: 'Cart ID', value: parent_transaction.cart_id, style: { fontSize: 12 } },
                            { label: 'Total amount', value: fmt(parent_transaction.amount) },
                            { label: 'Transaction fee', value: `- ${fmt(parent_transaction.transaction_fee)}`, color: '#dc3545' },
                            { label: 'Admin fee', value: `- ${fmt(parent_transaction.admin_fee)}`, color: '#dc3545' },
                        ].map(({ label, value, color, style }) => (
                            <div key={label} className="d-flex justify-content-between py-2 border-bottom">
                                <span className="text-muted" style={{ fontSize: 13 }}>{label}</span>
                                <span className="fw-medium" style={{ fontSize: 13, color, ...style }}>{value}</span>
                            </div>
                        ))}
                        <div className="d-flex justify-content-between pt-2 mt-1 border-top">
                            <span className="fw-medium" style={{ fontSize: 13 }}>Net to tutor</span>
                            <span className="fw-medium" style={{ fontSize: 13, color: '#198754' }}>
                                {fmt(parent_transaction.net_amount)}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Subscription Plan (subscription type only) */}
            {!isSession && subscription_plan && (
                <div className="card border shadow-sm mb-3" style={{ borderRadius: 12 }}>
                    <div className="card-body p-4">
                        <p className="text-uppercase text-muted fw-semibold mb-3" style={{ fontSize: 11, letterSpacing: '0.05em' }}>
                            Subscription plan
                        </p>
                        <div className="d-flex align-items-center gap-3">
                            <div style={{
                                width: 44, height: 44, borderRadius: 10, background: '#fff8ec',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20
                            }}>⭐</div>
                            <div>
                                <p className="mb-0 fw-semibold">{subscription_plan.name}</p>
                                <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                                    Plan ID #{subscription_plan.id} · {subscription_plan.duration_type} billing
                                </p>
                            </div>
                            <div className="ms-auto text-end">
                                <p className="mb-0 fw-semibold" style={{ fontSize: 19 }}>₹{subscription_plan.price}</p>
                                <p className="text-muted mb-0" style={{ fontSize: 11 }}>base price</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Transaction + Timestamps */}
            <div className="row g-3 mb-3">
                <div className="col-md-6">
                    <div className="card border shadow-sm h-100" style={{ borderRadius: 12 }}>
                        <div className="card-body p-4">
                            <p className="text-uppercase text-muted fw-semibold mb-3" style={{ fontSize: 11, letterSpacing: '0.05em' }}>
                                Transaction info
                            </p>
                            {[
                                { label: 'Record ID', value: `#${id}` },
                                { label: 'Tutor ID', value: tutor ? `#${tutor.id}` : 'N/A' },
                                { label: 'Parent ID', value: parent ? `#${parent.id}` : 'N/A' },
                                { label: 'Earning type', value: earning_type },
                            ].map(({ label, value }) => (
                                <div key={label} className="d-flex justify-content-between py-2 border-bottom">
                                    <span className="text-muted" style={{ fontSize: 13 }}>{label}</span>
                                    <span className="fw-medium" style={{ fontSize: 13, textTransform: 'capitalize' }}>{value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="card border shadow-sm h-100" style={{ borderRadius: 12 }}>
                        <div className="card-body p-4">
                            <p className="text-uppercase text-muted fw-semibold mb-3" style={{ fontSize: 11, letterSpacing: '0.05em' }}>
                                Timestamps
                            </p>
                            {[
                                { label: 'Created', value: fmtDate(createdAt) },
                                { label: 'Updated', value: fmtDate(updatedAt) },
                                { label: 'Status', value: is_deleted ? 'Deleted' : 'Active' },
                            ].map(({ label, value }) => (
                                <div key={label} className="d-flex justify-content-between py-2 border-bottom">
                                    <span className="text-muted" style={{ fontSize: 13 }}>{label}</span>
                                    <span className="fw-medium" style={{ fontSize: 13 }}>{value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Timeline */}
            <div className="card border shadow-sm mb-3" style={{ borderRadius: 12 }}>
                <div className="card-body p-4">
                    <p className="text-uppercase text-muted fw-semibold mb-3" style={{ fontSize: 11, letterSpacing: '0.05em' }}>
                        Timeline
                    </p>
                    <div className="d-flex gap-3">
                        <div className="d-flex flex-column align-items-center" style={{ paddingTop: 4 }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#198754', flexShrink: 0 }} />
                            <div style={{ width: 1, flex: 1, background: '#dee2e6', marginTop: 4 }} />
                        </div>
                        <div className="pb-3">
                            <p className="text-muted mb-1" style={{ fontSize: 12 }}>Purchase date</p>
                            <p className="mb-0 fw-medium" style={{ fontSize: 13 }}>{fmtDate(purchase_date)}</p>
                        </div>
                    </div>
                    <div className="d-flex gap-3">
                        <div style={{ paddingTop: 4 }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: expiry_date ? '#ffc107' : '#dee2e6', flexShrink: 0 }} />
                        </div>
                        <div>
                            <p className="text-muted mb-1" style={{ fontSize: 12 }}>Expiry date</p>
                            <p className="mb-0 fw-medium" style={{ fontSize: 13, color: expiry_date ? undefined : '#adb5bd' }}>
                                {expiry_date ? fmtDate(expiry_date) : 'N/A (session-based)'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    )
}

export default ViewEarningDetail